"use client";

import Image from "next/image";
import { useRouter } from "next/navigation";

import { useState } from "react";

import { useClerk, useUser } from "@clerk/nextjs";
import { useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  ArrowLeft,
  CreditCard,
  Info,
  Loader2,
  MoreHorizontal,
} from "lucide-react";
import { toast } from "sonner";

import MastercardIcon from "@/app/assets/vectors/mastercard.svg";
import VisaIcon from "@/app/assets/vectors/visa.svg";
import { Button } from "@/frontend/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/frontend/components/ui/dialog";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
} from "@/frontend/components/ui/drawer";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/frontend/components/ui/dropdown-menu";
import { Progress } from "@/frontend/components/ui/progress";
import { Skeleton } from "@/frontend/components/ui/skeleton";
import {
  useDisconnectGithub,
  useGithubTokenQuery,
} from "@/frontend/hooks/github";
import {
  useDisconnectGoogleDrive,
  useGoogleDriveTokenQuery,
} from "@/frontend/hooks/google-drive";
import {
  useCreateBillingPortalSession,
  usePlanQuery,
  useStripeSubscriptionQuery,
} from "@/frontend/hooks/subscription";
import {
  useExtraMinutesQuery,
  useRemainingMonthlyMinutesQuery,
  useUsageResetDateQuery,
} from "@/frontend/hooks/usage";
import { useIsMobile } from "@/frontend/hooks/utils";
import { CONNECTIONS } from "@/frontend/lib/connections";
import { getErrorMessage } from "@/frontend/lib/error-utils";
import { PLANS } from "@/frontend/lib/plans";
import {
  SETTINGS_TABS,
  SettingsTab,
  useSidebarStore,
} from "@/frontend/lib/stores";
import { cn } from "@/frontend/lib/utils";
import { Plan } from "@/shared/types";

function renderCardIcon(brand: string | undefined) {
  const iconProps = { className: "h-6 w-9" };

  switch (brand?.toLowerCase()) {
    case "visa":
      return <Image src={VisaIcon} alt="Visa" {...iconProps} />;
    case "mastercard":
      return <Image src={MastercardIcon} alt="Mastercard" {...iconProps} />;
    default:
      return (
        <div className="flex h-6 w-9 items-center justify-center rounded bg-muted-foreground/10">
          <CreditCard className="h-4 w-4 text-muted-foreground" />
        </div>
      );
  }
}

export function SettingsDialog() {
  const { user, isSignedIn } = useUser();
  const { signOut } = useClerk();
  const router = useRouter();
  const queryClient = useQueryClient();
  const isMobile = useIsMobile();
  const [mobileView, setMobileView] = useState<"tabs" | "content">("tabs");

  const {
    isSettingsDialogOpen,
    setIsSettingsDialogOpen,
    settingsDialogActiveTab,
    setSettingsDialogActiveTab,
    setIsUpgradeDialogOpen,
    setIsGetExtraDialogOpen,
    setConnectDialogOption,
  } = useSidebarStore();

  const getPlanQuery = usePlanQuery();
  const getStripeSubscriptionQuery = useStripeSubscriptionQuery();
  const getRemainingMonthlyMinutesQuery = useRemainingMonthlyMinutesQuery();
  const getExtraMinutesQuery = useExtraMinutesQuery();
  const getUsageResetDateQuery = useUsageResetDateQuery();

  // GitHub connection
  const githubTokenQuery = useGithubTokenQuery();
  const isGithubConnected = githubTokenQuery.data?.hasToken ?? false;

  // Google Drive connection
  const googleDriveTokenQuery = useGoogleDriveTokenQuery();
  const isGoogleDriveConnected = googleDriveTokenQuery.data?.hasToken ?? false;

  // Twitter connection

  // TRPC mutations
  const disconnectGithubMutation = useDisconnectGithub();
  const disconnectGoogleDriveMutation = useDisconnectGoogleDrive();
  const createBillingPortalSessionMutation = useCreateBillingPortalSession();

  if (!isSignedIn || !user) return null;

  const plan = getPlanQuery.data ? PLANS[getPlanQuery.data as Plan] : null;

  const planName = plan?.name ?? "Free";
  const planFullName = plan?.fullName ?? "Scout Free";

  const planAgentMinutes = plan?.agentMinutes ?? 60;

  const remainingMinutes =
    getRemainingMonthlyMinutesQuery.data ?? planAgentMinutes;

  const extraMinutes = getExtraMinutesQuery.data ?? null;

  const usageResetDate = getUsageResetDateQuery.data ?? null;

  // Handle mobile navigation
  const handleTabClick = (tabId: SettingsTab["id"]) => {
    setSettingsDialogActiveTab(tabId);
    if (isMobile) {
      setMobileView("content");
    }
  };

  const handleMobileBack = () => {
    setMobileView("tabs");
  };

  // Reset mobile view when dialog opens/closes
  if (!isSettingsDialogOpen && mobileView === "content") {
    setMobileView("tabs");
  }

  const TabsList = (
    <div className="flex flex-col p-4">
      {SETTINGS_TABS.map((tab) => (
        <Button
          key={tab.id}
          variant="ghost"
          className={cn(
            "w-full justify-start text-muted hover:bg-muted-foreground/5 hover:text-white",
            settingsDialogActiveTab === tab.id &&
              "bg-muted-foreground/10 text-white",
          )}
          onClick={() => handleTabClick(tab.id)}
        >
          <tab.icon />
          {tab.name}
        </Button>
      ))}
    </div>
  );

  const ContentHeader = (
    <div className="flex h-16 items-center gap-2 border-b border-muted-foreground/10 px-4">
      {isMobile && mobileView === "content" && (
        <Button
          variant="ghost"
          size="icon"
          onClick={handleMobileBack}
          className="text-muted-foreground"
        >
          <ArrowLeft />
        </Button>
      )}
      <h3 className="font-medium">
        {SETTINGS_TABS.find((tab) => tab.id === settingsDialogActiveTab)?.name}
      </h3>
    </div>
  );

  const ContentBody = (
    <div className="flex flex-col gap-6 overflow-y-auto p-6">
      {settingsDialogActiveTab === "general" && (
        <>
          {/* Profile Section */}
          <div className="flex items-center justify-between pb-6">
            <div className="flex items-center gap-4">
              <div className="relative h-12 w-12 overflow-hidden rounded-full">
                <Image
                  src={user.imageUrl}
                  alt={user.fullName || "Profile"}
                  fill
                  className="object-cover"
                />
              </div>
              <div className="flex flex-col gap-1">
                <div className="flex items-center gap-2">
                  <p className="font-medium">
                    {user.fullName || user.username}
                  </p>
                  <div className="flex items-center rounded-full bg-primary/10 px-2 py-0.5 text-sm font-medium text-primary">
                    {planName}
                  </div>
                </div>
                <p className="text-sm font-medium text-muted">
                  {user.primaryEmailAddress?.emailAddress}
                </p>
              </div>
            </div>
            <Button
              variant="outline"
              size="sm"
              className="text-foreground"
              onClick={() => {
                queryClient.clear();
                signOut();
                setIsSettingsDialogOpen(false);
              }}
            >
              Sign out
            </Button>
          </div>
          {/* Appearance Section */}
          {/* <div className="flex flex-col gap-6">
                  <h3 className="text-lg font-medium">Appearance</h3>
                  <div className="flex gap-4">
                    <div
                      className="flex cursor-pointer flex-col items-center gap-3"
                      onClick={() => setTheme("system")}
                    >
                      <div
                        className={cn(
                          "relative h-18 w-26 overflow-hidden rounded-xl border-2 transition-all",
                          theme === "system"
                            ? "border-white shadow-lg"
                            : "border-transparent hover:border-white/20",
                        )}
                      >
                        <Image
                          src={SystemIcon}
                          alt="System Default"
                          fill
                          className="object-cover"
                        />
                      </div>
                      <span
                        className={cn(
                          "text-sm transition-colors",
                          theme === "system"
                            ? "text-white"
                            : "text-muted-foreground",
                        )}
                      >
                        System default
                      </span>
                    </div>

                    <div
                      className="flex cursor-pointer flex-col items-center gap-3"
                      onClick={() => setTheme("light")}
                    >
                      <div
                        className={cn(
                          "relative h-18 w-26 overflow-hidden rounded-xl border-2 transition-all",
                          theme === "light"
                            ? "border-white shadow-lg"
                            : "border-transparent hover:border-white/20",
                        )}
                      >
                        <Image
                          src={LightIcon}
                          alt="Light"
                          fill
                          className="object-cover"
                        />
                      </div>
                      <span
                        className={cn(
                          "text-sm transition-colors",
                          theme === "light"
                            ? "text-white"
                            : "text-muted-foreground",
                        )}
                      >
                        Light
                      </span>
                    </div>

                    <div
                      className="flex cursor-pointer flex-col items-center gap-3"
                      onClick={() => setTheme("dark")}
                    >
                      <div
                        className={cn(
                          "relative h-18 w-26 overflow-hidden rounded-xl border-2 transition-all",
                          theme === "dark"
                            ? "border-white shadow-lg"
                            : "border-transparent hover:border-white/20",
                        )}
                      >
                        <Image
                          src={DarkIcon}
                          alt="Dark"
                          fill
                          className="object-cover"
                        />
                      </div>
                      <span
                        className={cn(
                          "text-sm transition-colors",
                          theme === "dark"
                            ? "text-white"
                            : "text-muted-foreground",
                        )}
                      >
                        Dark
                      </span>
                    </div>
                  </div>
                </div> */}
        </>
      )}

      {settingsDialogActiveTab === "billing" && (
        <div className="flex flex-col gap-6">
          {/* Current Subscription Section */}
          <div className="mb-6 flex flex-col gap-4">
            <div className="flex flex-col gap-4 rounded-2xl border border-muted-foreground/10 bg-muted-foreground/20 p-6">
              <div className="flex items-start justify-between">
                {getPlanQuery.isPending ? (
                  <Skeleton className="h-8 w-32" />
                ) : (
                  <>
                    <h2 className="text-xl font-medium">{planFullName}</h2>
                    <div className="flex items-center gap-2">
                      {getStripeSubscriptionQuery.data && (
                        <Button
                          onClick={() =>
                            createBillingPortalSessionMutation.mutate(
                              undefined,
                              {
                                onSuccess: (result) => {
                                  if (result) {
                                    router.push(result);
                                  }
                                },
                                onError: (error) => {
                                  toast.error(
                                    getErrorMessage(
                                      error,
                                      "Failed to create billing session",
                                    ),
                                  );
                                },
                              },
                            )
                          }
                          variant="ghost"
                          size="sm"
                          className="hover:bg-muted-foreground/30"
                        >
                          Manage
                        </Button>
                      )}
                      <Button
                        onClick={() => {
                          setIsUpgradeDialogOpen(true);
                          setIsSettingsDialogOpen(false);
                        }}
                        variant="outline"
                        className="text-foreground"
                        size="sm"
                      >
                        {plan?.id === "pro" ? "Switch plan" : "Upgrade"}
                      </Button>
                    </div>
                  </>
                )}
              </div>
              {getPlanQuery.isPending
                ? null
                : getStripeSubscriptionQuery.data?.currentPeriodEnd && (
                    <p className="text-sm">
                      You&apos;re subscribed to {planName}. Your subscription
                      will automatically renew on{" "}
                      {format(
                        new Date(
                          getStripeSubscriptionQuery.data.currentPeriodEnd,
                        ),
                        "MMMM d, yyyy",
                      )}
                      . Your current bill is $
                      {(
                        (getStripeSubscriptionQuery.data.currentBillAmount ??
                          0) / 100
                      ).toFixed(2)}
                      .
                      {getStripeSubscriptionQuery.data.isExpiring && (
                        <span className="text-destructive">
                          {" "}
                          Your subscription will be canceled at the end of the
                          current billing period.
                        </span>
                      )}
                    </p>
                  )}
            </div>
          </div>

          {/* Payment Methods Section */}
          {getStripeSubscriptionQuery.data?.paymentMethods && (
            <div className="flex flex-col gap-6 pb-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-medium">Payment methods</h3>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-foreground"
                  onClick={() =>
                    createBillingPortalSessionMutation.mutate(undefined, {
                      onSuccess: (result) => {
                        if (result) {
                          router.push(result);
                        }
                      },
                      onError: (error) => {
                        toast.error(
                          getErrorMessage(
                            error,
                            "Failed to create billing session",
                          ),
                        );
                      },
                    })
                  }
                >
                  Add payment method
                </Button>
              </div>
              <div className="flex flex-col rounded-2xl border border-muted-foreground/10 bg-muted-foreground/20">
                {getStripeSubscriptionQuery.data.paymentMethods.length > 0 ? (
                  getStripeSubscriptionQuery.data.paymentMethods.map(
                    (method) => (
                      <div
                        key={method.id}
                        className="flex gap-4 border-b border-muted-foreground/10 p-4 last:border-0"
                      >
                        {renderCardIcon(method.brand)}
                        <div className="flex flex-col">
                          <div className="flex items-center gap-2">
                            <p className="font-medium">
                              <span className="capitalize">{method.brand}</span>{" "}
                              ending in {method.last4}
                            </p>
                            {method.isDefault && (
                              <div className="flex items-center rounded-full bg-muted-foreground/10 px-1.5 py-0.5 text-xs font-medium text-muted">
                                Default
                              </div>
                            )}
                          </div>
                          <p className="text-sm text-muted-foreground">
                            Expires{" "}
                            {method.expMonth && method.expYear
                              ? format(
                                  new Date(method.expYear, method.expMonth - 1),
                                  "MMM",
                                )
                              : method.expMonth}
                            {method.expYear ? ` ${method.expYear}` : ""}
                          </p>
                        </div>
                      </div>
                    ),
                  )
                ) : (
                  <p className="text-sm text-muted-foreground">
                    No payment methods found
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Billing History Section */}
          {getStripeSubscriptionQuery.data?.billingHistory && (
            <div className="flex flex-col gap-6">
              <h3 className="text-lg font-medium">Billing</h3>
              <div className="flex flex-col gap-4 rounded-2xl border border-muted-foreground/10 bg-muted-foreground/20 px-6">
                <table className="w-full">
                  <thead className="border-b border-muted-foreground/10">
                    <tr>
                      <th className="py-5 text-left text-sm font-medium text-muted-foreground">
                        Description
                      </th>
                      <th className="py-5 text-left text-sm font-medium text-muted-foreground">
                        Date
                      </th>
                      <th className="py-5 text-left text-sm font-medium text-muted-foreground">
                        Status
                      </th>
                      <th className="py-5 text-right text-sm font-medium text-muted-foreground">
                        Amount
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {getStripeSubscriptionQuery.data.billingHistory.length >
                    0 ? (
                      getStripeSubscriptionQuery.data.billingHistory.map(
                        (invoice) => (
                          <tr
                            key={invoice.id}
                            className="border-b border-muted-foreground/10 last:border-0"
                          >
                            <td className="py-5 text-sm">
                              {invoice.productName}
                            </td>
                            <td className="py-5 text-sm">
                              {invoice.date
                                ? format(new Date(invoice.date), "MMM d, yyyy")
                                : "—"}
                            </td>
                            <td className="py-5">
                              <span
                                className={cn(
                                  "inline-flex rounded-full px-2 py-0.5 text-xs font-medium",
                                  invoice.status === "paid" &&
                                    "bg-emerald-400/10 text-emerald-400",
                                  invoice.status === "open" &&
                                    "bg-amber-400/10 text-amber-400",
                                  invoice.status === "void" &&
                                    "bg-muted/10 text-muted-foreground",
                                )}
                              >
                                {invoice.status === "paid"
                                  ? "Paid"
                                  : invoice.status === "open"
                                    ? "Pending"
                                    : "Void"}
                              </span>
                            </td>
                            <td className="py-5 text-right text-sm">
                              ${(invoice.amount / 100).toFixed(2)}
                            </td>
                          </tr>
                        ),
                      )
                    ) : (
                      <tr>
                        <td
                          colSpan={4}
                          className="px-6 py-8 text-center text-sm text-muted-foreground"
                        >
                          No billing history found
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {settingsDialogActiveTab === "usage" && (
        <div className="flex flex-col gap-6">
          <div className="grid gap-6 lg:grid-cols-2">
            {/* Included Usage Card */}
            <div className="flex flex-col gap-6 rounded-2xl border border-muted-foreground/10 bg-muted-foreground/20 p-6">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-medium">Included usage</h3>
                <Info className="h-3 w-3 text-muted-foreground" />
              </div>
              <div className="flex flex-col gap-3">
                {getRemainingMonthlyMinutesQuery.isPending ? (
                  <Skeleton className="h-6 w-32" />
                ) : (
                  <p className="text-xl font-medium">
                    {planAgentMinutes >= 60
                      ? (
                          Math.max(0, planAgentMinutes - remainingMinutes) / 60
                        ).toFixed(2)
                      : Math.max(0, planAgentMinutes - remainingMinutes)}{" "}
                    /{" "}
                    {planAgentMinutes >= 60
                      ? planAgentMinutes / 60
                      : planAgentMinutes}{" "}
                    <span className="text-sm text-muted-foreground">
                      agent {planAgentMinutes >= 60 ? "hours" : "minutes"} used
                    </span>
                  </p>
                )}

                <Progress
                  value={
                    getRemainingMonthlyMinutesQuery.isPending
                      ? 0
                      : ((planAgentMinutes - remainingMinutes) /
                          planAgentMinutes) *
                        100
                  }
                  className="h-1"
                />
              </div>
              {getUsageResetDateQuery.isPending ? (
                <Skeleton className="h-5 w-48" />
              ) : usageResetDate ? (
                <p className="text-sm text-muted">
                  Your agent hours will reset on{" "}
                  {format(usageResetDate, "d MMMM yyyy")}.
                </p>
              ) : (
                <p className="text-sm text-muted">Usage resets monthly.</p>
              )}
            </div>

            {/* Extra Agent Hours Card */}
            <div className="flex flex-col gap-6 rounded-2xl border border-muted-foreground/10 bg-muted-foreground/20 p-6">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-medium">Extra agent hours</h3>
                <Info className="h-3 w-3 text-muted-foreground" />
              </div>
              <div className="flex flex-col gap-3">
                {getExtraMinutesQuery.isPending ? (
                  <Skeleton className="h-6 w-32" />
                ) : (
                  <p className="text-xl font-medium">
                    {extraMinutes && extraMinutes > 0
                      ? extraMinutes >= 60
                        ? `${Math.round(extraMinutes / 60)}`
                        : extraMinutes
                      : "0"}{" "}
                    <span className="text-sm text-muted-foreground">
                      agent hours{" "}
                      {extraMinutes && extraMinutes > 0 ? "available" : "used"}
                    </span>
                  </p>
                )}

                <Progress value={0} className="h-1" />
              </div>
              <p className="text-sm text-muted">
                {plan?.id === "lite" || plan?.id === "pro"
                  ? "Pay for extra agent hours beyond your plan limits."
                  : "Upgrade to a paid plan to purchase extra agent hours."}
              </p>
              {(plan?.id === "lite" || plan?.id === "pro") && (
                <Button
                  onClick={() => {
                    setIsGetExtraDialogOpen(true);
                    setIsSettingsDialogOpen(false);
                  }}
                  variant="outline"
                  className="text-foreground"
                  size="sm"
                >
                  Get extra hours
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {settingsDialogActiveTab === "connections" && (
        <div className="flex flex-col rounded-2xl border border-muted-foreground/10 bg-muted-foreground/20">
          {CONNECTIONS.map((connection) => {
            const isConnected =
              connection.id === "github"
                ? isGithubConnected
                : connection.id === "drive"
                  ? isGoogleDriveConnected
                  : false;

            const disconnectMutation =
              connection.id === "github"
                ? disconnectGithubMutation
                : connection.id === "drive"
                  ? disconnectGoogleDriveMutation
                  : null;

            return (
              <div
                key={connection.id}
                className="flex items-center justify-between gap-2 border-b border-muted-foreground/10 p-4 last:border-0"
              >
                <div className="flex items-center gap-4">
                  <Image
                    src={connection.vector}
                    alt={connection.name}
                    className="mb-4 size-6"
                  />
                  <div className="flex flex-col">
                    <div className="flex items-center gap-2">
                      <p className="font-medium">{connection.name}</p>
                      {isConnected && (
                        <div className="rounded-full bg-emerald-400/10 px-1.5 py-0.5 text-xs text-emerald-400">
                          Connected
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {connection.id === "github" &&
                      githubTokenQuery.data?.username
                        ? githubTokenQuery.data.username
                        : isConnected
                          ? `${connection.name} connected`
                          : connection.description}
                    </p>
                  </div>
                </div>
                {connection.id === "github" &&
                (githubTokenQuery.isPending ||
                  disconnectGithubMutation.isPending) ? (
                  <Loader2 className="m-2 size-4 animate-spin" />
                ) : connection.id === "drive" &&
                  (googleDriveTokenQuery.isPending ||
                    disconnectGoogleDriveMutation.isPending) ? (
                  <Loader2 className="m-2 size-4 animate-spin" />
                ) : isConnected ? (
                  <Dialog>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="hover:bg-muted-foreground/10"
                        >
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent
                        align="end"
                        className="w-36"
                        sideOffset={4}
                        alignOffset={-4}
                        forceMount
                      >
                        <DialogTrigger asChild>
                          <DropdownMenuItem className="text-destructive hover:text-destructive">
                            Disconnect
                          </DropdownMenuItem>
                        </DialogTrigger>
                      </DropdownMenuContent>
                    </DropdownMenu>

                    <DialogContent className="sm:max-w-md" elevated>
                      <DialogHeader className="hidden">
                        <DialogTitle>Disconnect {connection.name}</DialogTitle>
                      </DialogHeader>
                      <div className="flex flex-col items-center gap-4 py-4">
                        <div className="flex gap-3">
                          <div className="mb-4 flex size-16 items-center justify-center rounded-2xl bg-background text-primary">
                            <Image
                              src="/logo.svg"
                              alt="Scout Logo"
                              width={32}
                              height={32}
                            />
                          </div>
                          <div className="h-16 content-center pt-2">
                            <svg
                              width="33"
                              height="8"
                              viewBox="0 0 33 8"
                              fill="none"
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path
                                d="M28.4492 1.48047C29.2889 2.02789 30.9114 3.06019 31.7216 3.62734C32.5318 4.19449 32.4807 4.26522 32.3982 4.35235C32.2118 4.54927 31.2298 5.27198 29.8393 6.32244C29.3068 6.74625 29.1257 6.94825 28.9558 7.15884"
                                stroke="white"
                                stroke-linecap="round"
                              />
                              <path
                                d="M0.417286 4.93015C0.371786 4.9421 0.330061 4.96829 0.301787 5.00681C0.27351 5.04529 0.260934 5.09243 0.26694 5.13873C0.272945 5.18502 0.297132 5.22739 0.334292 5.25739C0.371456 5.28741 0.418483 5.30209 0.465526 5.30204C0.465526 5.30204 0.465526 5.30204 0.465526 5.30204C0.630256 5.30168 0.794856 5.30028 0.959292 5.29788C2.43946 5.27632 3.90718 5.17195 5.36369 5.00043C5.45595 4.98958 5.54826 4.97845 5.6402 4.96711C7.00308 4.79856 8.36067 4.57613 9.71636 4.2882C9.87713 4.25401 10.0379 4.21888 10.1986 4.18276C10.2445 4.1724 10.2871 4.14768 10.3167 4.11017C10.3463 4.0727 10.3605 4.02602 10.3561 3.97954C10.3517 3.93307 10.329 3.88988 10.2929 3.85861C10.2568 3.82731 10.2104 3.81101 10.1633 3.80943C10.1633 3.80943 10.1633 3.80943 10.1633 3.80943C9.99858 3.80407 9.83396 3.79981 9.66943 3.79668C8.28259 3.77002 6.89917 3.8261 5.52521 3.97578C5.43256 3.98585 5.33964 3.99636 5.24688 4.00728C3.78214 4.17977 2.33274 4.45196 0.895828 4.80822C0.736178 4.84782 0.576656 4.88847 0.417286 4.93015Z"
                                fill="white"
                              />
                              <path
                                d="M21.5488 3.41406C21.5021 3.41987 21.4572 3.44044 21.4242 3.47502C21.3911 3.50956 21.3725 3.55478 21.3725 3.60156C21.3725 3.64835 21.3911 3.69356 21.4242 3.72811C21.4572 3.76269 21.5021 3.78326 21.5488 3.78906C21.5488 3.78906 21.5488 3.78906 21.5488 3.78906C21.7031 3.81461 21.8817 3.84086 22.0472 3.86528C22.3075 3.90312 22.5637 3.9384 22.8246 3.9714C24.0816 4.13128 25.3419 4.23451 26.6013 4.28824C27.1099 4.3101 27.6209 4.32383 28.1271 4.33231C28.8836 4.34952 29.6488 4.33401 30.369 4.39075C30.6082 4.41893 30.8589 4.44872 31.1119 4.473C31.205 4.48195 31.2984 4.49013 31.3919 4.49742C31.4268 4.5001 31.464 4.50336 31.4939 4.50653C31.5208 4.4891 31.5559 4.58424 31.459 4.40743C31.4747 4.45185 31.5044 4.49123 31.5453 4.51609C31.5861 4.54095 31.6343 4.54938 31.68 4.53932C31.7257 4.52927 31.7658 4.50141 31.7925 4.46169C31.8191 4.42197 31.8296 4.37375 31.8252 4.32683C31.8252 4.32683 31.8252 4.32683 31.8252 4.32683C31.6997 4.07327 31.6409 4.15734 31.5981 4.11712C31.5535 4.10054 31.5154 4.08802 31.4772 4.07536C31.3872 4.04666 31.2998 4.01821 31.2124 3.9896C30.9753 3.91198 30.7375 3.83291 30.4965 3.75706C29.706 3.54563 28.9453 3.46591 28.1784 3.39061C27.6678 3.34391 27.155 3.31112 26.6441 3.28916C25.3795 3.23521 24.1128 3.24788 22.8473 3.31994C22.5847 3.33469 22.3266 3.35208 22.0642 3.37183C21.8933 3.38482 21.731 3.39841 21.5488 3.41406Z"
                                fill="white"
                              />
                              <path
                                d="M12.1223 1.52782C12.0573 1.50534 11.9855 1.49973 11.9184 1.51943C11.8513 1.53908 11.7948 1.58166 11.7603 1.63969C11.7257 1.69772 11.7153 1.76766 11.73 1.83601C11.7447 1.90441 11.7839 1.96485 11.8347 2.01124C11.8347 2.01124 11.8347 2.01124 11.8347 2.01124C11.9624 2.12104 12.0746 2.2171 12.1955 2.31754C12.489 2.5617 12.7876 2.79757 13.0912 3.02541C13.8956 3.62967 14.737 4.17833 15.6063 4.66742C16.8038 5.34114 18.0625 5.90647 19.3825 6.36503C19.5292 6.41605 19.6765 6.46558 19.8248 6.51402C19.8903 6.53522 19.9622 6.53943 20.0289 6.51842C20.0956 6.49746 20.1513 6.45379 20.1846 6.39509C20.218 6.33639 20.2271 6.26626 20.211 6.19821C20.195 6.13011 20.1546 6.07046 20.1029 6.02507C19.9854 5.92225 19.8674 5.82103 19.7482 5.72102C18.6771 4.8216 17.5407 4.03458 16.3418 3.3601C15.4716 2.87048 14.5636 2.43787 13.6286 2.06702C13.2759 1.9269 12.919 1.79519 12.5583 1.67185C12.4132 1.6224 12.2586 1.57034 12.1223 1.52782Z"
                                fill="white"
                              />
                              <path
                                d="M18.2876 1.35392C18.3206 1.29465 18.3404 1.22652 18.3355 1.1578C18.3307 1.08911 18.3023 1.02607 18.2544 0.980605C18.2066 0.935144 18.1422 0.909933 18.0733 0.908593C18.0045 0.907215 17.9374 0.930424 17.8799 0.966395C17.8799 0.966395 17.8799 0.966395 17.8799 0.966395C17.7528 1.06421 17.667 1.12279 17.5627 1.20262C17.3565 1.35845 17.156 1.51812 16.9602 1.68231C16.2968 2.23827 15.6899 2.84497 15.1419 3.49925C14.7469 3.97081 14.3823 4.46735 14.0481 4.98929C13.7798 5.41182 13.536 5.83142 13.3074 6.3102C13.2981 6.33047 13.289 6.35061 13.2805 6.3702C13.233 6.47341 13.1865 6.59207 13.1561 6.69577C13.1373 6.76096 13.1334 6.83179 13.1537 6.89765C13.1739 6.96347 13.2158 7.01847 13.2727 7.05196C13.3296 7.08546 13.398 7.09548 13.4654 7.08125C13.5328 7.06705 13.5928 7.02931 13.6408 6.98129C13.6408 6.98129 13.6408 6.98129 13.6408 6.98129C13.7137 6.90874 13.7775 6.8551 13.8534 6.79296C13.8682 6.78109 13.8838 6.76822 13.8991 6.75567C14.27 6.4596 14.6604 6.1353 15.0194 5.7995C15.4724 5.37982 15.8971 4.93374 16.2918 4.46238C16.8396 3.80836 17.3307 3.10477 17.7623 2.355C17.8898 2.13376 18.0121 1.90858 18.1297 1.67912C18.1839 1.57095 18.2536 1.44151 18.2876 1.35392Z"
                                fill="white"
                              />
                            </svg>
                          </div>
                          <div className="mb-4 flex size-16 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                            <connection.Icon className="size-8" />
                          </div>
                        </div>
                        <h3 className="text-lg font-semibold">
                          Disconnect {connection.name}
                        </h3>
                        <p className="text-center text-sm text-muted-foreground">
                          Are you sure you want to disconnect your{" "}
                          <span className="font-semibold text-muted">
                            {connection.name}
                          </span>{" "}
                          account? You can reconnect it anytime.
                        </p>
                      </div>
                      <DialogFooter className="mt-4 flex flex-col gap-2 sm:flex-col">
                        <DialogClose asChild>
                          <Button
                            variant="destructive"
                            onClick={() =>
                              disconnectMutation?.mutate(undefined, {
                                onSuccess: () => {
                                  toast.success(
                                    connection.id === "github"
                                      ? "GitHub disconnected"
                                      : "Google Drive disconnected",
                                  );
                                },
                                onError: () => {
                                  toast.error(
                                    connection.id === "github"
                                      ? "Failed to disconnect GitHub"
                                      : "Failed to disconnect Google Drive",
                                  );
                                },
                              })
                            }
                            disabled={disconnectMutation?.isPending}
                          >
                            {disconnectMutation?.isPending ? (
                              <Loader2 className="h-4 w-4 animate-spin" />
                            ) : (
                              "Disconnect"
                            )}
                          </Button>
                        </DialogClose>
                        <DialogClose asChild>
                          <Button
                            variant="ghost"
                            className="bg-muted-foreground/20 hover:bg-muted-foreground/10"
                          >
                            Cancel
                          </Button>
                        </DialogClose>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                ) : (
                  <Button
                    onClick={() => setConnectDialogOption(connection.id)}
                    variant="outline"
                    size="sm"
                    className="text-foreground"
                  >
                    Connect
                  </Button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <Drawer
        open={isSettingsDialogOpen}
        onOpenChange={setIsSettingsDialogOpen}
      >
        <DrawerContent className="flex h-full flex-col overflow-hidden p-0">
          <DrawerTitle className="sr-only">Settings</DrawerTitle>
          {mobileView === "tabs" ? (
            <>
              <DrawerHeader className="flex h-16 items-center gap-2 border-b border-muted-foreground/10 px-4">
                <h3 className="font-medium">Scout Settings</h3>
              </DrawerHeader>
              <div className="flex-1 overflow-y-auto">{TabsList}</div>
            </>
          ) : (
            <>
              {ContentHeader}
              <div className="flex-1 overflow-y-auto">{ContentBody}</div>
            </>
          )}
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={isSettingsDialogOpen} onOpenChange={setIsSettingsDialogOpen}>
      <DialogContent className="flex h-[48rem] gap-0 overflow-hidden !p-0 sm:max-w-5xl">
        <div className="flex min-h-full w-80 flex-col border-r border-muted-foreground/10">
          {TabsList}
        </div>
        <div className="flex w-full flex-col">
          <DialogHeader className="min-h-16 border-b border-muted-foreground/10">
            <DialogTitle className="pl-6">
              {
                SETTINGS_TABS.find((tab) => tab.id === settingsDialogActiveTab)
                  ?.name
              }
            </DialogTitle>
          </DialogHeader>
          {ContentBody}
        </div>
      </DialogContent>
    </Dialog>
  );
}
